import React, { useCallback, useId, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { DateRedoItem } from 'fym-shared/src/music24/dateRedo';
import { TypedMemo } from '../../../../../common/types';
import { DateRedoItemLine } from './DateRedoItemLine';
import { dateRedoMessages as messages } from './dateRedoMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the date redo; each part uses its own slice of it */
import styles from './DateRedo.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The "Before release" group of the Kit tab (option A of the date redo): the pre-save files that
 * show the old date after the release day. They are not made again, so the group only tells the
 * artist not to post them. Closed by default.
 */
const BeforeReleaseGroupComponent: React.FC<{ items: DateRedoItem[]; newDateLabel: string | null }> = ({
  items,
  newDateLabel,
}) => {
  const intl = useIntl();
  const bodyId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen((current) => !current), []);
  if (items.length === 0) {
    return null;
  }
  return (
    <section className={clsx(styles.before, isOpen && styles.beforeOpen)} data-testid="date-redo-before-release">
      <button
        type="button"
        className={styles.beforeHead}
        aria-expanded={isOpen}
        aria-controls={bodyId}
        onClick={toggle}
      >
        <b>{intl.formatMessage(messages.beforeReleaseTitle)}</b>
        <span>{intl.formatMessage(messages.beforeReleaseCount, { count: items.length })}</span>
        <span className={styles.beforeChevron} aria-hidden="true">
          ⌄
        </span>
      </button>
      {isOpen && (
        <div id={bodyId} className={styles.beforeBody}>
          <p className={styles.beforeNote}>{intl.formatMessage(messages.beforeReleaseNote)}</p>
          {items.map((item) => (
            <DateRedoItemLine key={item.uuid} item={item} newDateLabel={newDateLabel} />
          ))}
        </div>
      )}
    </section>
  );
};

export const BeforeReleaseGroup = TypedMemo(BeforeReleaseGroupComponent);
